import React, { useMemo } from 'react'
import {
  Label as SemanticLabel,
  LabelProps as SemanticLabelProps,
} from 'semantic-ui-react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import { colorVars, colorShortToLong, INHERIT } from '../../../utils/colors'
import { ColorNames } from '../../../constants/colorVars'
import Icon, { IconProps } from '../icon'
import { LightenDarkenColor } from './lightDarkenColor'
import styles from './styles.scss'
import type { AutomationTarget } from '../../../types'

const cx = classnames.bind(styles)

const DARKEN_PERCENTAGE = -20

interface RibbonContentProps {
  title?: string
  iconProps?: IconProps
  className?: string
}

/**
 * Use this as a standard child of the Ribbon component.
 * Handles optional icon and title.
 */
const RibbonContent: React.FC<RibbonContentProps> = ({
  title,
  iconProps,
  className,
}) => {
  return (
    <div className={cx('ribbon-content', className)}>
      {typeof iconProps === 'object' && (
        <Icon
          fitted={
            _.isNil(iconProps.fitted) ? true : (iconProps.fitted as boolean)
          }
          name={iconProps.name}
          iconStyle={iconProps.iconStyle || 'solid'}
          color={iconProps.color || 'white'}
        />
      )}
      <div>{title}</div>
    </div>
  )
}

interface RibbonProps
  extends Omit<SemanticLabelProps, 'color'>,
    AutomationTarget {
  className?: string
  children?: React.ReactNode
  color?: ColorNames | 'inherit'
  darkText?: boolean
  style?: React.CSSProperties
}

const Ribbon: React.FC<RibbonProps> = ({
  className = '',
  children,
  color: colorName = ColorNames.Gray,
  darkText = false,
  style,
  elmId = '',
  ...rest
}) => {
  const colorKey = useMemo(() => colorShortToLong(colorName || ''), [colorName])
  const color = useMemo(
    () =>
      colorName === INHERIT
        ? INHERIT
        : colorVars[colorKey as keyof typeof colorVars] || '#000000',
    [colorName, colorKey]
  )

  const shadedColor = useMemo(
    () => LightenDarkenColor(color, DARKEN_PERCENTAGE),
    [color]
  )

  return (
    <SemanticLabel
      data-elm-id={elmId || 'ribbon'}
      ribbon
      className={cx('ribbon', { 'dark-text': darkText }, className)}
      style={{ backgroundColor: color, borderColor: shadedColor, ...style }}
      {...rest}
    >
      {children}
    </SemanticLabel>
  )
}

interface RibbonStatic {
  Content: typeof RibbonContent
}

const RibbonWithStatic = Ribbon as React.FC<RibbonProps> & RibbonStatic

RibbonWithStatic.Content = RibbonContent

export default RibbonWithStatic
